import { useEffect, useRef, useState } from "react";

import { ITmpSession } from "../../../@types/admin/AdminTmpIntegration";
import { useTmpSessionPayload } from "../../../hooks/queries/admin/tmpIntegration";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  ErrorMessage,
  FilterButton,
  ModalContainer,
  ModalFormActions,
  ModalHeader,
  ModalSubtitle,
  ModalTitle,
  TableLoadingState,
} from "../shared";
import {
  DrillDownCaption,
  PayloadContainer,
} from "./AdminJupyterIntegration.styles";
import { CopyLinkButton } from "./AdminTmpIntegration.styles";
import {
  formatJsonString,
  patientDisplayName,
} from "./TmpSessionDrillDownModal";

export const NO_PAYLOAD_MESSAGE =
  "No raw payload stored for this session (received before payload capture was added)";

interface ITmpSessionPayloadModalProps {
  session: ITmpSession;
  onClose: () => void;
}

/**
 * The raw webhook body TMP sent for one order (EN-5203), prettified. The API
 * masks SSINs before storing it, so what is shown here is what support may
 * quote. Older sessions predate payload capture and have nothing to show.
 */
export function TmpSessionPayloadModal({
  session,
  onClose,
}: ITmpSessionPayloadModalProps) {
  const { payload, isLoading, isError } = useTmpSessionPayload(
    session.telemonitoringId
  );
  const [copied, setCopied] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(
    () => () => {
      if (resetTimer.current) clearTimeout(resetTimer.current);
    },
    []
  );

  const pretty = payload?.payload ? formatJsonString(payload.payload) : null;

  const handleCopy = async () => {
    if (!pretty) return;
    try {
      await navigator.clipboard?.writeText(pretty);
      setCopied(true);
      if (resetTimer.current) clearTimeout(resetTimer.current);
      resetTimer.current = setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <ModalContainer data-testid="tmp-session-payload">
      <ModalHeader>
        <ModalTitle>Raw payload — {patientDisplayName(session)}</ModalTitle>
        <ModalSubtitle>
          Telemonitoring {session.telemonitoringId} · MRN{" "}
          {session.externalPatientId}
        </ModalSubtitle>
      </ModalHeader>

      {isLoading && <TableLoadingState />}
      {isError && (
        <ErrorMessage>
          Failed to load the raw payload for this order.
        </ErrorMessage>
      )}

      {payload && !pretty && (
        <DrillDownCaption data-testid="tmp-payload-empty">
          {NO_PAYLOAD_MESSAGE}
        </DrillDownCaption>
      )}

      {pretty && (
        <>
          <DrillDownCaption>
            Received {getLongFormattedDate(payload?.receivedAt, "—")}. SSINs are
            masked.
          </DrillDownCaption>
          <PayloadContainer data-testid="tmp-payload-json">
            {pretty}
          </PayloadContainer>
        </>
      )}

      <ModalFormActions>
        {pretty && (
          <CopyLinkButton
            type="button"
            copied={copied}
            aria-label="Copy payload"
            onClick={handleCopy}
          >
            {copied ? "Copied" : "Copy"}
          </CopyLinkButton>
        )}
        <FilterButton variant="secondary" onClick={onClose}>
          Close
        </FilterButton>
      </ModalFormActions>
    </ModalContainer>
  );
}
